import React, { useEffect, useMemo, useState } from 'react';
import { useProductConfig } from '@/features/_data/useProductConfig';
import { useUserInfo } from '@/features/_data/useUserInfo';
import { T } from '@/ui/Text/Text';
import { IconPlus } from '@/icons';
import { Flex } from '@/ui/Flex/Flex';
import { SensorType } from '@/types/SensorType';
import { MagicModel } from '@/types/MagicModel';
import { StructureData } from '@/types/BaseSlide';
import { createId } from '@paralleldrive/cuid2';
import { Tooltip } from '@/ui/Tooltip/Tooltip';
import { TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { BaseSlideGroupProps } from '@/types/SlideGroupProps';
import { sortByIndexOptional } from '@/utils/arrayUtils';
import { useIntl } from 'react-intl';
import { FrontendSlide } from '@/types/SlideType';
import { EditorSlideComponent } from '../../../EditorSlideComponent';
import SensorGroupMappingSidebar from '../../../SensorMappingSidebar/SensorGroupMappingSidebar';
import { useSlideGroupBase } from '../../hooks/useSlideGroupBase';
import {
  StyledConnector,
  StyledTrihowPad,
  StyledTrihowPadContent,
  StyledTrihowPadGroupNameInput,
  StyledTrihowPadHeader,
  StyledTrihowPadLogo,
  StyledTrihowPadSlideWrapper,
  StyledTrihowPadWrapper,
  StyledSplitScreenSlideWrapper,
} from './trihowpad-styles';
import SlideGroupKebapMenu from '../../SlideGroupKebapMenu';

export const TrihowPadSlideGroup: React.FC<BaseSlideGroupProps> = (props) => {
  const { handleDeleteGroup, handleMoveUp, handleMoveDown } =
    useSlideGroupBase(props);

  const {
    unitId,
    slideGroup,
    disallowSlideDeletion,
    isFirstGroup,
    isLastGroup,
    channelSelective,
    languageSupport,
    images,
    setImages,
    videos,
    setVideos,
    webslides,
    setWebslides,
    magicModels,
    onClick,
    onUpdateSlide,
    onUpdateSlideGroupName,
    onUpdateSlideOrder,
    onVideoOptionsChange,
    setMagicModels,
    onChangeNameInLibrary,
    mapSlidesToChannelsAndRename,
  } = props;

  const { config } = useProductConfig(unitId);
  const { isAdmin } = useUserInfo();
  const intl = useIntl();
  const toast = useToast();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const slides = useMemo(() => {
    const arr = new Array(config.maxSlidesPerGroup + 1).fill(null);
    Object.values(slideGroup.slides)
      .sort(sortByIndexOptional)
      .slice(0, config.maxSlidesPerGroup + 1)
      .forEach((slide) => {
        if (typeof slide.index === 'undefined' || slide.index == null) return;
        if (slide.index >= 0 && slide.index < config.maxSlidesPerGroup + 1) {
          arr[slide.index] = slide;
        }
      });
    return arr;
  }, [slideGroup, config.maxSlidesPerGroup]);

  const assignedTag = useMemo(() => {
    return (
      magicModels.find((MM: MagicModel) => MM.modelName === slideGroup.name) ||
      null
    );
  }, [magicModels, slideGroup.name]);

  const [draftName, setDraftName] = useState(assignedTag?.modelName || '');

  useEffect(() => {
    setDraftName(assignedTag?.modelName || '');
  }, [assignedTag?.modelName]);

  const updateSlideGroupName = (inputValue: string) => {
    if (inputValue.trim() === '') {
      setDraftName(assignedTag?.modelName || '');
      toast({
        mode: TOASTTYPE.Danger,
        title: intl.formatMessage({
          defaultMessage: 'The name cannot be empty.',
          id: '06d/+t',
        }),
        subtitle: intl.formatMessage({
          defaultMessage: 'Try another name.',
          id: '9vihwE',
        }),
      });
      return;
    }

    const newModels = magicModels.map((MM: MagicModel) => {
      if (MM.sensorId === assignedTag?.sensorId) {
        return {
          modelName: inputValue,
          sensorId: assignedTag.sensorId,
          sensorType: assignedTag.sensorType,
        };
      }
      return MM;
    });

    setMagicModels(newModels);
    onUpdateSlideGroupName(slideGroup.id, inputValue);
  };

  const onUpdateSlideAddTag = (
    slideId: string | null,
    slideData: Partial<FrontendSlide> | null,
    isFromLibrary?: boolean,
    slidePosition?: number,
  ) => {
    if (assignedTag && slideData) {
      const slideProps: Omit<StructureData, 'triggerIds'> = {
        id: createId(),
        children: [],
        initialState: null,
        sensorIds: [],
        slideGroupId: '',
        slideIds: [],
      };
      onUpdateSlide(
        slideId,
        {
          ...slideData,
          structureData: {
            ...slideProps,
            triggerIds: [
              {
                type:
                  slidePosition === 4
                    ? SensorType.SPLITSCREEN
                    : SensorType.NEXMO_NFC,
                values: [
                  slidePosition === 4
                    ? assignedTag.sensorId
                    : `${assignedTag.sensorId}|${(slidePosition ?? 0) + 1}`,
                ],
              },
            ],
          },
        },
        isFromLibrary,
        slidePosition,
      );
    } else {
      onUpdateSlide(slideId, slideData, isFromLibrary, slidePosition);
    }
  };

  const splitScreenSlide = slides[4] ?? null;
  let updateSplitScreenSlide = onUpdateSlideAddTag;
  let splitScreenSlideUpdateSlideOrder = onUpdateSlideOrder;
  if (splitScreenSlide) {
    updateSplitScreenSlide = onUpdateSlide;
    splitScreenSlideUpdateSlideOrder = (
      slideGroupId: string | undefined,
      order: 'right' | 'left' | undefined,
      slideidx: number | undefined,
    ) =>
      onUpdateSlideOrder(slideGroupId, order, slideidx, assignedTag?.sensorId);
  }

  return (
    <StyledTrihowPadWrapper>
      <SensorGroupMappingSidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        slideGroup={slideGroup}
        magicModels={magicModels}
        assignedTag={assignedTag}
        mapSlidesToChannelsAndRename={mapSlidesToChannelsAndRename}
      />

      <StyledTrihowPad>
        <StyledTrihowPadHeader>
          <Tooltip
            title={intl.formatMessage({
              defaultMessage: 'Click the + on the right to add a sensor.',
              id: '6f1xI0',
            })}
          >
            <StyledTrihowPadGroupNameInput
              disabled={!assignedTag}
              placeholder={intl.formatMessage({
                defaultMessage: 'New Group (assign tag)',
                id: 'pJ7Vhc',
              })}
              value={draftName}
              onChange={(e) => {
                setDraftName(e.target.value);
              }}
              onBlur={(e) => {
                if (!assignedTag) return;
                updateSlideGroupName(e.target.value);
              }}
            />
          </Tooltip>

          <StyledTrihowPadLogo>
            <svg
              width={24}
              height={24}
              xmlns="http://www.w3.org/2000/svg"
              data-name="Layer 1"
              id="a"
              viewBox="0 0 100 100"
            >
              <path
                className="b"
                d="m50.66,0c.19.21.36.44.56.64,16.42,16.43,32.85,32.85,49.28,49.28.2.2.44.38.86.74-.35.24-.64.37-.86.59-16.43,16.42-32.86,32.85-49.28,49.27-.2.2-.38.43-.56.64h-.16c-.12-.14-.24-.28-.36-.41C33.56,84.17,16.98,67.59.41,51.02c-.13-.13-.27-.24-.41-.36,0-.05,0-.1,0-.16.14-.12.28-.24.41-.36C16.98,33.56,33.56,16.98,50.13.41c.13-.13.24-.27.36-.41.05,0,.1,0,.16,0ZM9.64,52.2c13.11,13.11,26.12,26.12,39.2,39.2,3.08-16.08,6.16-32.16,9.26-48.32-16.21,3.05-32.31,6.08-48.46,9.12Zm54.13-9.32c-2.92,15.31-5.82,30.54-8.66,45.49,12.13-12.13,24.39-24.39,36.63-36.63-9.17-2.91-18.49-5.86-27.96-8.86Zm-14.39-33.55c-12.23,12.23-24.44,24.44-36.64,36.64,15.1-2.86,30.28-5.73,45.52-8.61-3.01-9.48-5.96-18.79-8.89-28.03Zm7.38,4.81c.1.34.16.59.24.83,2.26,7.13,4.53,14.26,6.78,21.4.18.56.45.83,1.01,1.01,7.01,2.21,14.02,4.44,21.03,6.66.37.12.74.21,1.18.34-10.1-10.11-20.13-20.13-30.24-30.24Z"
              />
            </svg>
            <T>TRIHOW</T>
          </StyledTrihowPadLogo>
          <Flex align="center" gap="0px 16px">
            <Tooltip title="Add sensor to all slides in the group.">
              <span
                style={{ cursor: 'pointer' }}
                onClick={() => setIsSidebarOpen(true)}
              >
                <IconPlus />
              </span>
            </Tooltip>
            <SlideGroupKebapMenu
              handleMoveUp={handleMoveUp}
              handleMoveDown={handleMoveDown}
              handleDeleteGroup={handleDeleteGroup}
              isLastGroup={isLastGroup}
              isFirstGroup={isFirstGroup}
              isAdmin={isAdmin}
              disallowSlideDeletion={disallowSlideDeletion}
            />
          </Flex>
        </StyledTrihowPadHeader>
        <StyledTrihowPadContent maxSlidesPerGroup={config.maxSlidesPerGroup}>
          {slides.length &&
            slides.slice(0, 4).map((slide, slideIndex) => {
              let key = `empty-${slideGroup.id}-${slideIndex}`;
              const componentSlide = slide || null;
              const isFirst = slideIndex === 0;
              // exclude split screen slide from slide movement
              const isLast = slideIndex === slides.length - 2;
              let updateComponentSlide = onUpdateSlideAddTag;
              let componentUpdateSlideOrder = onUpdateSlideOrder;
              if (slide) {
                key = slide.slideId;
                updateComponentSlide = onUpdateSlide;
                componentUpdateSlideOrder = (
                  slideGroupId: string | undefined,
                  order: 'right' | 'left' | undefined,
                  slideidx: number | undefined,
                ) =>
                  onUpdateSlideOrder(
                    slideGroupId,
                    order,
                    slideidx,
                    assignedTag?.sensorId,
                  );
              }

              return (
                <React.Fragment key={key}>
                  <StyledConnector isFirst={slideIndex === 0} />
                  <StyledTrihowPadSlideWrapper isFirst={slideIndex === 0}>
                    <EditorSlideComponent
                      slide={componentSlide}
                      slideGroupId={slideGroup.id}
                      slidePosition={slideIndex}
                      magicModels={magicModels}
                      languageSupport={languageSupport}
                      disallowSlideDeletion={disallowSlideDeletion}
                      channelSelective={channelSelective}
                      isFirst={isFirst}
                      isLast={isLast}
                      isTrihowPad
                      images={images}
                      videos={videos}
                      webslides={webslides}
                      setImages={setImages}
                      setVideos={setVideos}
                      setWebslides={setWebslides}
                      onClick={onClick}
                      onChangeNameInLibrary={onChangeNameInLibrary}
                      onUpdateSlide={updateComponentSlide}
                      onUpdateSlideOrder={componentUpdateSlideOrder}
                      onVideoOptionsChange={onVideoOptionsChange}
                    />
                  </StyledTrihowPadSlideWrapper>
                </React.Fragment>
              );
            })}
        </StyledTrihowPadContent>
      </StyledTrihowPad>
      <StyledSplitScreenSlideWrapper>
        <Tooltip
          title={intl.formatMessage({
            defaultMessage:
              'Placing a Slide here, will automatically use this slide whenever the sensor is used as a splitscreen.',
            id: 'FodHtv',
          })}
        >
          <T h3>
            {intl.formatMessage({
              defaultMessage: 'Splitscreen Slide',
              id: 'mtl6bM',
            })}
          </T>
        </Tooltip>
        <EditorSlideComponent
          slide={splitScreenSlide}
          slideGroupId={slideGroup.id}
          slidePosition={4}
          magicModels={magicModels}
          languageSupport={languageSupport}
          disallowSlideDeletion={disallowSlideDeletion}
          channelSelective={channelSelective}
          isTrihowPad
          isDesignatedSplitScreen
          images={images}
          videos={videos}
          webslides={webslides}
          setImages={setImages}
          setVideos={setVideos}
          setWebslides={setWebslides}
          onClick={onClick}
          onChangeNameInLibrary={onChangeNameInLibrary}
          onUpdateSlide={updateSplitScreenSlide}
          onUpdateSlideOrder={splitScreenSlideUpdateSlideOrder}
          onVideoOptionsChange={onVideoOptionsChange}
        />
      </StyledSplitScreenSlideWrapper>
    </StyledTrihowPadWrapper>
  );
};
